/**
 * ロジック図 1 枚を React Flow の節点と線に組む。
 *
 * **種類はタグで見せる**（Dhik 2026-10-01「図形は適当なのでいい感じにタグで差し替えたい」）。箱の形は
 * 揃え、上端に種類のタグ（入力 ・ 業務ルール ・ 業務ルールの出力 ・ 分岐 ・ 作用 ・ 終了）を置く。ただし
 * **分岐だけは Figma の凡例どおりひし形にする**（Dhik 2026-10-01「分岐はひし形にしろよ」）。分かれ目が
 * 図の中で一目で拾えるようにするためで、タグも中に残す。地の色は
 * 業務ルールの出力だけ変える — 凡例で唯一「塗り」の種類で、途中のデータが業務ルールの箱と並んで
 * 見分けにくくなるのを防ぐ。
 *
 * **線は業務フロー図の作図規約で引く**（`logic-edges.ts`）。分岐から出る線には条件のラベルを付ける。
 * 業務ルールの中身（表）は図に入れず、箱を押して開く。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ロジック図の節点を種類のタグつきの箱に、線を条件のラベルつきの矢印に組む。
 * @graph-connects computeLogicLayout [calls] 箱の位置と大きさ
 * @graph-connects routeLogicEdges [calls] 作図規約に沿った線の経路
 */
import { Handle, MarkerType, Position, type Edge, type Node, type NodeProps } from '@xyflow/react';
import type { ReactNode } from 'react';

import { FLOW_EDGE_TYPE, type FlowGutterEdgeData } from '../FlowGutterEdge.js';
import { routeLogicEdges } from './logic-edges.js';
import { atOr, type LogicDiagramModel, type LogicNodeKind } from './logic-model.js';
import { computeLogicLayout, type LogicLayout } from './logic-layout.js';

/**
 * 図に出す文言（種類のタグ）。訳は画面が引いて渡す。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ロジック図の種類のタグの文言を表す。
 * @graph-connects none
 */
export type LogicKindLabels = Readonly<Record<LogicNodeKind, string>>;

/** React Flow の節点の型の名前。 @graph-connects none */
const LOGIC_NODE_TYPE = 'logic';

/**
 * 独自の節点に渡す中身。
 * @graph-connects none
 */
interface LogicNodeData extends Record<string, unknown> {
  readonly body: ReactNode;
}

/** 線の出入り口を見せない（押して繋ぐ図ではない）。 @graph-connects none */
const HIDDEN_HANDLE_STYLE = { opacity: 0, pointerEvents: 'none' } as const;

/**
 * ロジック図の節点 1 つ。左で受け、右へ出す。
 *
 * @param props React Flow が渡す節点の props
 * @returns 節点
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ロジック図の節点の中身と、左右の線の出入り口を描く。
 * @graph-connects none
 */
function LogicFlowNode({ data }: NodeProps<Node<LogicNodeData>>) {
  return (
    <>
      <Handle type="target" position={Position.Left} style={HIDDEN_HANDLE_STYLE} />
      {data.body}
      <Handle type="source" position={Position.Right} style={HIDDEN_HANDLE_STYLE} />
    </>
  );
}

/**
 * ロジック図が使う独自の節点の型。`FlowCanvas` の `nodeTypes` に渡す。
 * @graph-connects none
 */
export const LOGIC_NODE_TYPES = { [LOGIC_NODE_TYPE]: LogicFlowNode };

/** 種類ごとの箱の地と枠。 @graph-connects none */
const BOX_CLASS: Readonly<Record<LogicNodeKind, string>> = {
  input: 'rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-surface)]',
  rule: 'rounded border border-[var(--color-text-secondary)] bg-[var(--color-bg-surface)]',
  ruleOutput: 'rounded-2xl border border-transparent bg-[var(--color-text-secondary)]',
  /* 分岐は枠を持たず、ひし形を `branchBody` が描く。 */
  branch: '',
  effect: 'rounded border border-[var(--color-text-secondary)] bg-[var(--color-bg-surface)]',
  end: 'rounded border-2 border-[var(--color-text-secondary)] bg-[var(--color-bg-surface)]',
};

/** 業務ルールの出力は地が濃いので字を反転する。 @graph-connects none */
const TEXT_CLASS: Readonly<Record<LogicNodeKind, string>> = {
  input: 'text-[var(--color-text-primary)]',
  rule: 'text-[var(--color-text-primary)]',
  ruleOutput: 'text-[var(--color-bg-surface)]',
  branch: 'text-[var(--color-text-primary)]',
  effect: 'text-[var(--color-text-primary)]',
  end: 'text-[var(--color-text-primary)]',
};

/** 箱の既定の見た目（React Flow の白い枠）を消す。 @graph-connects none */
const NODE_STYLE_RESET = {
  padding: 0,
  border: 'none',
  background: 'transparent',
  boxShadow: 'none',
} as const;

/**
 * 分岐の箱の中身。箱いっぱいのひし形を描き、タグ ・ 名前 ・ 中の行をひし形の内側の中央に置く。
 * @graph-connects none
 */
function branchBody(title: string, lines: readonly string[], labels: LogicKindLabels): ReactNode {
  return (
    <div
      className={`relative h-full w-full text-xs leading-[18px] ${TEXT_CLASS.branch}`}
      data-logic-kind="branch"
      data-logic-shape="diamond"
    >
      <svg
        className="absolute inset-0 h-full w-full"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        aria-hidden
      >
        <polygon
          points="50,1 99,50 50,99 1,50"
          fill="var(--color-bg-surface)"
          stroke="var(--color-text-secondary)"
          strokeWidth={1.5}
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <div className="absolute inset-y-0 left-1/4 right-1/4 flex flex-col items-center justify-center gap-0.5 text-center">
        <span
          className="rounded-full border border-current px-2 text-[10px] leading-4 opacity-80"
          data-logic-tag
        >
          {labels.branch}
        </span>
        {title !== '' && <span className="font-bold">{title}</span>}
        {lines.map((line) => (
          <span key={line}>{line}</span>
        ))}
      </div>
    </div>
  );
}

/**
 * 節点の箱の中身（種類のタグ ・ 名前 ・ 中の行）。分岐はひし形で描く。
 * @graph-connects branchBody [calls] 分岐のひし形
 */
function nodeBody(
  kind: LogicNodeKind,
  title: string,
  lines: readonly string[],
  labels: LogicKindLabels,
): ReactNode {
  if (kind === 'branch') return branchBody(title, lines, labels);
  return (
    <div
      className={`flex h-full w-full flex-col gap-0.5 px-3 py-2 text-left text-xs leading-[18px] ${BOX_CLASS[kind]} ${TEXT_CLASS[kind]}`}
      data-logic-kind={kind}
    >
      <span
        className="self-start rounded-full border border-current px-2 text-[10px] leading-4 opacity-80"
        data-logic-tag
      >
        {labels[kind]}
      </span>
      {title !== '' && <span className="font-bold">{title}</span>}
      {lines.map((line) => (
        <span key={line}>{line}</span>
      ))}
    </div>
  );
}

/**
 * 図を組むときの任意の口。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ロジック図を組むときの任意の口（置き場所を決める材料・業務ルールを押したとき・片側にしか無い節点の印）を表す。
 * @graph-connects none
 */
export interface LogicBuildOptions {
  /**
   * 置き場所を決める材料。案件の左右比較では変更前と変更後を合わせた材料を渡し、2 面を同じ置き場所で
   * 描く。省けば描く材料そのもので決める
   */
  readonly layoutModel?: LogicDiagramModel;
  /** 業務ルールを開ける箱（業務ルール ・ 作用 ・ 分岐 ・ 取り出し）を押したとき。省けば押せない */
  readonly onSelectRule?: (ruleId: number) => void;
  /** 片側にしか無い節点の鍵と、どちらの面か（変更後は増えた、変更前は消えた） */
  readonly diff?: { readonly side: 'before' | 'after'; readonly onlyHere: ReadonlySet<string> };
}

/** 片側にしか無い節点の枠。 @graph-connects none */
const ONLY_HERE_CLASS: Readonly<Record<'before' | 'after', string>> = {
  before: 'rounded outline outline-2 outline-offset-2 outline-[var(--color-diff-removed-pane)]',
  after: 'rounded outline outline-2 outline-offset-2 outline-[var(--color-diff-added-pane)]',
};

/**
 * ロジック図 1 枚を、置き場所つきの React Flow の節点と線に組む。
 *
 * @param model 材料
 * @param labels 種類のタグの文言
 * @param options 置き場所を決める材料・業務ルールを押したとき・片側にしか無い節点の印
 * @returns 節点・線・置き場所
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ロジック図の材料を、種類のタグつきの箱・条件のラベルつきの矢印に組み、業務ルールを押す口と案件の印を付ける。
 * @graph-connects computeLogicLayout [calls] 箱の位置と大きさ
 * @graph-connects routeLogicEdges [calls] 作図規約に沿った線の経路
 * @graph-connects nodeBody [calls] 節点の箱の中身
 */
export function buildLogicDiagram(
  model: LogicDiagramModel,
  labels: LogicKindLabels,
  options: LogicBuildOptions = {},
): { nodes: Node[]; edges: Edge[]; layout: LogicLayout } {
  const placed = options.layoutModel ?? model;
  const layout = computeLogicLayout(placed);

  const nodes: Node[] = model.nodes.flatMap((node) => {
    const box = layout.boxes.get(node.key);
    if (box === undefined) return [];
    const ruleId = node.ruleId ?? null;
    const inner = nodeBody(node.kind, node.title, node.lines, labels);
    const onSelect = options.onSelectRule;
    const clickable =
      ruleId !== null && onSelect !== undefined ? (
        <button
          type="button"
          className="h-full w-full cursor-pointer text-left"
          data-logic-rule={ruleId}
          onClick={() => {
            onSelect(ruleId);
          }}
        >
          {inner}
        </button>
      ) : (
        inner
      );
    const marked =
      options.diff?.onlyHere.has(node.key) === true ? (
        <div className={`h-full w-full ${ONLY_HERE_CLASS[options.diff.side]}`} data-logic-only-here>
          {clickable}
        </div>
      ) : (
        clickable
      );
    const data: LogicNodeData = { body: marked };
    return [
      {
        id: node.key,
        type: LOGIC_NODE_TYPE,
        position: { x: box.x, y: box.y },
        data,
        style: { ...NODE_STYLE_RESET, width: box.w, height: box.h },
        draggable: false,
        /* 押せる箱を選べる節点のままにする。選べない節点へのポインタは図の面へ抜け、中のボタンが押せなくなる。 */
        selectable: ruleId !== null && onSelect !== undefined,
        zIndex: 1,
      },
    ];
  });
  /*
   * 線は業務フロー図の作図規約で引く（`logic-edges.ts`）。経路 ・ ラベルの位置 ・ 枝の色はそこが決め、
   * ここは業務フロー図と同じ線の部品（`gutter`）へ渡すだけにする。
   */
  const routes = routeLogicEdges(model.nodes, model.edges, layout);
  const edges: Edge[] = routes.map((route) => {
    const edge = atOr(model.edges, route.index, { from: '', to: '' });
    const data: FlowGutterEdgeData = {
      path: route.path,
      label: route.label,
      labelX: route.labelX,
      labelY: route.labelY,
      branchColor: route.branchColor,
    };
    return {
      id: `edge:${String(route.index)}`,
      source: edge.from,
      target: edge.to,
      type: FLOW_EDGE_TYPE,
      data,
      markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16 },
      zIndex: 0,
    };
  });
  return { nodes, edges, layout };
}
